import { metersToFeetInches } from './floorPlanUtils';
import {
  CUSTOM_FIXTURE_TYPES,
  NON_CUSTOM_FIXTURE_TYPES,
  FURNISHING_TYPES,
  HORIZONTAL_ONLY_FIXTURE_TYPES,
} from './measurementConstants';
import { HIGHLIGHT_COLORS } from '../constants/highlightColors';
import { assignStaggerSlots } from './measurementUtilsV2';

// Priority for node assignment when multiple objects share the same position
// Higher priority objects "own" the node and get measurements generated for them
export const TYPE_PRIORITY = {
  'lower-cabinet': 100,
  'upper-cabinet': 99,
  cabinet: 98,
  countertop: 97,
  'shower-niche': 91,
  bathtub: 90,
  shower: 89,
  toilet: 85,
  sink: 80,
  stove: 75,
  oven: 74,
  dishwasher: 73,
  mirror: 65,
  'wall-light': 60,
  door: 50,
  window: 49,
};

// Helper function to get object type label (moved up for use by classification helpers)
export const getObjectTypeLabel = (obj) => {
  if (obj.type) {
    if (typeof obj.type === 'string') {
      return obj.type.charAt(0).toUpperCase() + obj.type.slice(1);
    } else if (typeof obj.type === 'object') {
      const key = Object.keys(obj.type)[0];
      return key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Object';
    }
  }
  if (obj.category) {
    if (obj.category.door) return 'Door';
    if (obj.category.window) return 'Window';
  }
  return 'Object';
};

/**
 * Get the normalized fixture type for classification (lowercase, exact match)
 */
const getNormalizedFixtureType = (obj) => {
  if (obj.type) {
    if (typeof obj.type === 'string') {
      return obj.type.toLowerCase();
    } else if (typeof obj.type === 'object') {
      const key = Object.keys(obj.type)[0];
      return key ? key.toLowerCase() : null;
    }
  }
  if (obj.category) {
    if (obj.category.door) return 'door';
    if (obj.category.window) return 'window';
  }
  return null;
};

/**
 * Check if an object is a custom fixture (cabinets, countertops, shower niches)
 * Custom fixtures get their own measurement line with edge-to-edge dimensions
 */
export const isCustomFixture = (obj) => {
  const type = getNormalizedFixtureType(obj);
  return type && CUSTOM_FIXTURE_TYPES.has(type);
};

/**
 * Check if an object is a non-custom fixture (sinks, toilets, etc.)
 * Non-custom fixtures are grouped by type with centerline measurements
 */
const isNonCustomFixture = (obj) => {
  const type = getNormalizedFixtureType(obj);
  return type && NON_CUSTOM_FIXTURE_TYPES.has(type);
};

/**
 * Check if an object is a furnishing (no measurements)
 */
const isFurnishing = (obj) => {
  const type = getNormalizedFixtureType(obj);
  return type && FURNISHING_TYPES.has(type);
};

/**
 * Check if an object should only have horizontal measurements (no vertical)
 * These are typically fixtures mounted on other fixtures (e.g., faucets on sinks)
 */
export const isHorizontalOnlyFixture = (obj) => {
  const type = getNormalizedFixtureType(obj);
  return type && HORIZONTAL_ONLY_FIXTURE_TYPES.has(type);
};

/**
 * Check if an object should have measurements generated
 * Excludes countertops (have their own measurement line) and furnishings (no measurements)
 * Used by both horizontal and vertical measurement generation
 */
export const isMeasurableObject = (obj) => {
  const type = getNormalizedFixtureType(obj);
  // If type is unknown, include it (don't filter out objects with unrecognized types)
  const isCountertop = type && type.includes('countertop');
  return !isCountertop && !isFurnishing(obj);
};

/**
 * Filter objects to those within segment bounds that should have measurements
 * Unified filtering logic for both horizontal and vertical measurements
 */
export const getMeasurableObjectsInBounds = (
  projectedObjects,
  boundsStart,
  boundsEnd
) => {
  return projectedObjects.filter((obj) => {
    const objX = obj.x2D;
    const inBounds = objX >= boundsStart && objX <= boundsEnd;
    return inBounds && isMeasurableObject(obj);
  });
};

/**
 * Unified measurement generation for both horizontal and vertical directions
 * @param {Object} config - Configuration object
 * @param {Array} config.objects - Objects to measure
 * @param {number} config.scale - Scale factor (pixels per meter)
 * @param {number} config.nodeTolerance - Tolerance for clustering nodes (in pixels)
 * @param {Function} config.getPosition - Get primary position from object (e.g., obj => obj.x2D)
 * @param {Function} config.getSize - Get object size (e.g., getObjectWidth or getObjectHeight)
 * @param {Function} config.getSidePosition - Get position for side determination (e.g., obj => obj.y2D)
 * @param {number} config.sideCenter - Center value for side determination
 * @param {Object} config.labels - Labels for edges and endpoints
 * @param {string} config.labels.startEdge - Label for start edge (e.g., 'Left' or 'Top')
 * @param {string} config.labels.endEdge - Label for end edge (e.g., 'Right' or 'Bottom')
 * @param {string} config.labels.wallStart - Label for wall start (e.g., 'Wall Edge' or 'Ceiling')
 * @param {string} config.labels.wallEnd - Label for wall end (e.g., 'Wall Edge' or 'Floor')
 * @param {Object} config.sides - Side configuration
 * @param {string} config.sides.primary - Primary side name (e.g., 'top' or 'left')
 * @param {string} config.sides.secondary - Secondary side name (e.g., 'bottom' or 'right')
 * @param {number} config.wallStart - Wall start position in pixels
 * @param {number} config.wallEnd - Wall end position in pixels
 * @param {string} config.measurementType - 'horizontal' or 'vertical'
 * @param {string} config.wallId - Wall ID for the measurements
 * @param {Function} [config.customDetermineNodeSide] - Optional custom function to determine node side
 * @returns {Object} Object with nodes, typesWithNodes, and generateMeasurements function
 */
const createUnifiedMeasurementNodes = (config) => {
  const {
    objects,
    scale,
    nodeTolerance,
    getPosition,
    getSize,
    getSidePosition,
    sideCenter,
    labels,
    sides,
    customDetermineNodeSide,
  } = config;

  // Step 1: Generate all measurement positions with metadata
  // Each object generates either edge positions (custom fixtures) or center position (non-custom)
  const measurementPositions = [];

  objects.forEach((obj) => {
    const type = getObjectTypeLabel(obj).toLowerCase();
    const isCustom = isCustomFixture(obj);
    const size = getSize(obj, scale);
    const centerPos = getPosition(obj);
    const startEdge = centerPos - size / 2;
    const endEdge = centerPos + size / 2;

    const objectInfo = {
      obj,
      type,
      isCustom,
      centerPos,
      size,
    };

    if (isCustom) {
      // Custom fixtures (cabinets, countertops, showers) get edge positions
      measurementPositions.push({
        position: startEdge,
        edgeType: 'start', // Left or Top
        label: labels.startEdge,
        ...objectInfo,
      });
      measurementPositions.push({
        position: endEdge,
        edgeType: 'end', // Right or Bottom
        label: labels.endEdge,
        ...objectInfo,
      });
    } else {
      // Non-custom fixtures get center position
      measurementPositions.push({
        position: centerPos,
        edgeType: 'center',
        label: 'Center',
        ...objectInfo,
      });
    }
  });

  // Sort by position
  measurementPositions.sort((a, b) => a.position - b.position);

  // Step 2: Group positions within tolerance into nodes
  const nodes = [];
  measurementPositions.forEach((item) => {
    const existingNode = nodes.find(
      (node) => Math.abs(node.position - item.position) <= nodeTolerance
    );

    if (existingNode) {
      existingNode.objects.push(item);
      // Update node position to average of all positions in this node
      existingNode.position =
        existingNode.objects.reduce((sum, o) => sum + o.position, 0) /
        existingNode.objects.length;
    } else {
      nodes.push({
        position: item.position,
        objects: [item], // Array of measurement positions (not raw objects)
        assignedType: null,
        hasCustom: false,
        primaryObject: null,
        assignedSide: null,
      });
    }
  });

  // Helper to determine which side a node should be on
  const determineNodeSide = (nodeObjects) => {
    if (nodeObjects.length === 0) return sides.secondary;

    // Use custom function if provided
    if (customDetermineNodeSide) {
      return customDetermineNodeSide(nodeObjects, sides);
    }

    // Default: use average position compared to center
    const avgSidePos =
      nodeObjects.reduce(
        (sum, o) => sum + (getSidePosition(o.obj) || sideCenter),
        0
      ) / nodeObjects.length;

    return avgSidePos < sideCenter ? sides.primary : sides.secondary;
  };

  // Step 3: Assign type, hasCustom, primaryObject, and side to each node
  nodes.forEach((node) => {
    let highestPriority = -1;
    let assignedType = null;

    node.objects.forEach((item) => {
      const priority = TYPE_PRIORITY[item.type] || 0;
      if (priority > highestPriority) {
        highestPriority = priority;
        assignedType = item.type;
      }
    });

    node.assignedType = assignedType;
    node.hasCustom = node.objects.some((o) => o.isCustom);
    // Primary object is the one with highest priority
    node.primaryObject = node.objects.reduce((best, item) => {
      const priority = TYPE_PRIORITY[item.type] || 0;
      const bestPriority = TYPE_PRIORITY[best.type] || 0;
      return priority > bestPriority ? item : best;
    }, node.objects[0]);
    node.assignedSide = determineNodeSide(node.objects);
  });

  const typesWithNodes = [...new Set(nodes.map((n) => n.assignedType))];

  return { nodes, typesWithNodes, labels, sides };
};

/**
 * Generate measurements from nodes for a specific side
 * @param {Object} config - Configuration object
 * @param {Array} config.nodes - Nodes created by createUnifiedMeasurementNodes
 * @param {Array} config.typesWithNodes - Unique types with nodes
 * @param {string} config.side - Which side to generate for (e.g., 'top', 'bottom', 'left', 'right')
 * @param {Object} config.labels - Labels for edges
 * @param {number} config.wallStart - Wall start position
 * @param {number} config.wallEnd - Wall end position
 * @param {number} config.measurementLinePos - Position of the measurement line (e.g., y for horizontal)
 * @param {number} config.baseOffset - Base offset for measurement lines
 * @param {number} config.scale - Scale factor
 * @param {string} config.measurementType - 'horizontal' or 'vertical'
 * @param {string} config.wallId - Wall ID
 * @param {boolean} config.invertOffset - Whether to invert offset (for top/left sides)
 * @returns {Object} { measurements, lineCount }
 */
const generateMeasurementsForSide = (config) => {
  const {
    nodes,
    typesWithNodes,
    side,
    labels,
    wallStart,
    wallEnd,
    measurementLinePos,
    baseOffset,
    scale,
    measurementType,
    wallId,
    invertOffset,
  } = config;

  const measurements = [];
  const lineSpacing = 40;
  let currentLineIndex = 0;

  typesWithNodes.forEach((fixtureType) => {
    const typeName = fixtureType.charAt(0).toUpperCase() + fixtureType.slice(1);

    // Get nodes for this type AND this side
    // Each node now represents a single measurement position (edge or center)
    const typeNodes = nodes
      .filter((n) => n.assignedType === fixtureType && n.assignedSide === side)
      .sort((a, b) => a.position - b.position);

    if (typeNodes.length === 0) return;

    const lineOffset = invertOffset
      ? -(baseOffset + currentLineIndex * lineSpacing)
      : baseOffset + currentLineIndex * lineSpacing;

    // Build measurement points - each node is already a measurement position
    const measurementPoints = [
      {
        position: wallStart,
        type: 'wall-start',
        label: labels.wallStart,
      },
    ];

    // Group nodes by object to number them correctly (e.g., "Cabinet 1 Left", "Cabinet 1 Right")
    const objectNodeMap = new Map(); // objectId -> array of nodes for that object
    typeNodes.forEach((node) => {
      const primary = node.primaryObject;
      const objId = primary.obj.id;
      if (!objectNodeMap.has(objId)) {
        objectNodeMap.set(objId, []);
      }
      objectNodeMap.get(objId).push(node);
    });

    // Assign object numbers based on position of first node
    const objectOrder = [...objectNodeMap.keys()].sort((a, b) => {
      const aFirstNode = objectNodeMap.get(a)[0];
      const bFirstNode = objectNodeMap.get(b)[0];
      return aFirstNode.position - bFirstNode.position;
    });

    typeNodes.forEach((node) => {
      const primary = node.primaryObject;
      const objId = primary.obj.id;
      const objectNumber = objectOrder.indexOf(objId) + 1;
      const showNumber = objectOrder.length > 1;

      // Build label based on edge type
      let nodeLabel;
      if (primary.edgeType === 'start') {
        nodeLabel = showNumber
          ? `${typeName} ${objectNumber} ${labels.startEdge}`
          : `${typeName} ${labels.startEdge}`;
      } else if (primary.edgeType === 'end') {
        nodeLabel = showNumber
          ? `${typeName} ${objectNumber} ${labels.endEdge}`
          : `${typeName} ${labels.endEdge}`;
      } else {
        nodeLabel = showNumber
          ? `${typeName} ${objectNumber} Center`
          : `${typeName} Center`;
      }

      // Find the furthest fixture from the measurement line for extension lines
      // For horizontal: furthest y from measurementLinePos
      // For vertical: furthest x from measurementLinePos
      let furthestObject = primary.obj;
      let furthestDistance = 0;

      node.objects.forEach((item) => {
        const obj = item.obj;
        const distance =
          measurementType === 'horizontal'
            ? Math.abs((obj.y2D || 0) - measurementLinePos)
            : Math.abs((obj.x2D || 0) - measurementLinePos);

        if (distance > furthestDistance) {
          furthestDistance = distance;
          furthestObject = obj;
        }
      });

      measurementPoints.push({
        position: node.position,
        type: `object-${primary.edgeType}`,
        label: nodeLabel,
        object: primary.obj,
        objects: node.objects,
        node: node,
        furthestObject: furthestObject, // For extension line targeting
      });
    });

    measurementPoints.push({
      position: wallEnd,
      type: 'wall-end',
      label: labels.wallEnd,
    });

    // Generate measurements between consecutive points
    for (let i = 0; i < measurementPoints.length - 1; i++) {
      const start = measurementPoints[i];
      const end = measurementPoints[i + 1];
      const distance = Math.abs(end.position - start.position) / scale;

      if (distance > 0.01) {
        const measurement = {
          type: measurementType,
          subtype: 'node-based',
          fixtureType: fixtureType,
          typeIndex: currentLineIndex,
          assignedSide: side,
          distance,
          fromLabel: start.label,
          toLabel: end.label,
          offset: lineOffset,
          fromPoint: { ...start, position: undefined },
          toPoint: { ...end, position: undefined },
          wallId: wallId,
        };

        // Set start/end coordinates based on measurement type
        // Use furthestObject for extension line targeting (goes to furthest fixture from measurement line)
        if (measurementType === 'horizontal') {
          measurement.start = { x: start.position, y: measurementLinePos };
          measurement.end = { x: end.position, y: measurementLinePos };
          measurement.fromPoint.x = start.position;
          measurement.fromPoint.y =
            start.furthestObject?.y2D ||
            start.object?.y2D ||
            measurementLinePos;
          measurement.toPoint.x = end.position;
          measurement.toPoint.y =
            end.furthestObject?.y2D || end.object?.y2D || measurementLinePos;
        } else {
          measurement.start = { x: measurementLinePos, y: start.position };
          measurement.end = { x: measurementLinePos, y: end.position };
          measurement.fromPoint.x =
            start.furthestObject?.x2D ||
            start.object?.x2D ||
            measurementLinePos;
          measurement.fromPoint.y = start.position;
          measurement.toPoint.x =
            end.furthestObject?.x2D || end.object?.x2D || measurementLinePos;
          measurement.toPoint.y = end.position;
        }

        measurements.push(measurement);
      }
    }

    currentLineIndex++;
  });

  return { measurements, lineCount: currentLineIndex };
};

/**
 * Calculate measurements for wall elevations
 * @param {Object} wallData - Wall data including dimensions and objects
 * @param {Array} wallSegments - Array of wall segments for grouped walls
 * @param {Array} projectedObjects - Objects projected onto the wall
 * @param {string} filterMode - 'all', 'walls', 'openings'
 * @returns {Array} Array of measurement objects
 */
export const calculateElevationMeasurements = (
  wallData,
  wallSegments,
  projectedObjects,
  filterMode = 'all',
  renderScale = 100 // Accept the actual scale from ElevationsViewer
) => {
  const measurements = [];

  // Calculate total width for grouped walls
  const totalGroupWidth = wallSegments.reduce((sum, seg) => sum + seg.width, 0);
  const isGroupedWalls = wallSegments.length > 1;

  // Process each wall segment
  wallSegments.forEach((segment, segmentIndex) => {
    const { width, height, x, wall, index } = segment;
    const isFirstSegment = segmentIndex === 0;
    const isLastSegment = segmentIndex === wallSegments.length - 1;

    // Note: width and height are in meters, x is the cumulative x position in meters
    // We need to work in pixels for SVG rendering using the actual render scale
    const scale = renderScale; // Use the passed scale from ElevationsViewer
    const pixelX = x * scale;
    const pixelWidth = width * scale;
    const pixelHeight = height * scale;

    // Wall width measurements (horizontal) using unified node-based approach
    if (filterMode === 'walls' || filterMode === 'all') {
      // Find measurable objects on this segment (exclude furnishings and countertops)
      const segmentObjects = getMeasurableObjectsInBounds(
        projectedObjects,
        pixelX,
        pixelX + pixelWidth
      );

      const baseOffset = 40;

      // Create unified nodes for horizontal measurements
      const { nodes, typesWithNodes } = createUnifiedMeasurementNodes({
        objects: segmentObjects,
        scale,
        nodeTolerance: 0.001 * scale, // 1mm in pixels
        getPosition: (obj) => obj.x2D,
        getSize: getObjectWidth,
        getSidePosition: (obj) => obj.y2D,
        sideCenter: pixelHeight / 2,
        labels: {
          startEdge: 'Left',
          endEdge: 'Right',
          wallStart: 'Wall Edge',
          wallEnd: 'Wall Edge',
        },
        sides: { primary: 'top', secondary: 'bottom' },
      });

      // Generate TOP measurements (for objects in top half, measurements above wall)
      const topResult = generateMeasurementsForSide({
        nodes,
        typesWithNodes,
        side: 'top',
        labels: {
          startEdge: 'Left',
          endEdge: 'Right',
          wallStart: 'Wall Edge',
          wallEnd: 'Wall Edge',
        },
        wallStart: pixelX,
        wallEnd: pixelX + pixelWidth,
        measurementLinePos: 0,
        baseOffset,
        scale,
        measurementType: 'horizontal',
        wallId: wall?.id || `wall_${index}`,
        invertOffset: true,
      });
      measurements.push(...topResult.measurements);

      // Generate BOTTOM measurements (for objects in bottom half, measurements below wall)
      const bottomResult = generateMeasurementsForSide({
        nodes,
        typesWithNodes,
        side: 'bottom',
        labels: {
          startEdge: 'Left',
          endEdge: 'Right',
          wallStart: 'Wall Edge',
          wallEnd: 'Wall Edge',
        },
        wallStart: pixelX,
        wallEnd: pixelX + pixelWidth,
        measurementLinePos: pixelHeight,
        baseOffset,
        scale,
        measurementType: 'horizontal',
        wallId: wall?.id || `wall_${index}`,
        invertOffset: false,
      });
      measurements.push(...bottomResult.measurements);

      // Add overall wall width measurement at the bottom
      const totalOffset = baseOffset + bottomResult.lineCount * 40;
      const wallMeasurement = {
        type: 'horizontal',
        subtype: 'wall-total',
        start: { x: pixelX, y: pixelHeight },
        end: { x: pixelX + pixelWidth, y: pixelHeight },
        distance: width, // Already in meters
        label: 'Total Wall Width',
        offset: totalOffset,
        priority: 1, // Higher priority for display
        wallId: wall?.id || `wall_${index}`,
        fromPoint: { type: 'wall-start', x: pixelX },
        toPoint: { type: 'wall-end', x: pixelX + pixelWidth },
      };
      measurements.push(wallMeasurement);
    }

    // Wall height measurements (vertical) using unified node-based approach
    // Split objects left/right based on which side they're closer to
    if (
      (filterMode === 'walls' || filterMode === 'all') &&
      (!isGroupedWalls || isFirstSegment || isLastSegment)
    ) {
      // Calculate bounds for object filtering
      const boundsStart = isGroupedWalls ? wallSegments[0].x * scale : pixelX;
      const boundsEnd = isGroupedWalls
        ? (wallSegments[wallSegments.length - 1].x +
            wallSegments[wallSegments.length - 1].width) *
          scale
        : pixelX + pixelWidth;

      // Find all measurable objects (exclude furnishings and countertops)
      // Also exclude horizontal-only fixtures (e.g., faucets) from vertical measurements
      const allObjects = getMeasurableObjectsInBounds(
        projectedObjects,
        boundsStart,
        boundsEnd
      ).filter((obj) => !isHorizontalOnlyFixture(obj));

      const baseOffset = 0; // Start at wall edge

      // Calculate wall edges for left/right determination
      const wallLeftEdge = boundsStart;
      const wallRightEdge = boundsEnd;

      // Custom side determination for vertical measurements
      // Based on whether the rightmost object is closer to the left edge
      // or the leftmost object is closer to the right edge
      const verticalDetermineNodeSide = (nodeObjects, sides) => {
        if (nodeObjects.length === 0) return sides.secondary;

        const leftmostX = Math.min(...nodeObjects.map((o) => o.obj.x2D));
        const rightmostX = Math.max(...nodeObjects.map((o) => o.obj.x2D));

        const rightmostDistanceToLeft = rightmostX - wallLeftEdge;
        const leftmostDistanceToRight = wallRightEdge - leftmostX;

        return rightmostDistanceToLeft <= leftmostDistanceToRight
          ? sides.primary
          : sides.secondary;
      };

      // Create unified nodes for vertical measurements
      const { nodes, typesWithNodes } = createUnifiedMeasurementNodes({
        objects: allObjects,
        scale,
        nodeTolerance: 0.001 * scale, // 1mm in pixels
        getPosition: (obj) => obj.y2D,
        getSize: getObjectHeight,
        getSidePosition: (obj) => obj.x2D,
        sideCenter: (wallLeftEdge + wallRightEdge) / 2,
        labels: {
          startEdge: 'Top',
          endEdge: 'Bottom',
          wallStart: 'Ceiling',
          wallEnd: 'Floor',
        },
        sides: { primary: 'left', secondary: 'right' },
        customDetermineNodeSide: verticalDetermineNodeSide,
      });

      // Generate LEFT measurements (first segment or single wall)
      if (!isGroupedWalls || isFirstSegment) {
        const leftX = pixelX;
        const leftResult = generateMeasurementsForSide({
          nodes,
          typesWithNodes,
          side: 'left',
          labels: {
            startEdge: 'Top',
            endEdge: 'Bottom',
            wallStart: 'Ceiling',
            wallEnd: 'Floor',
          },
          wallStart: 0, // Ceiling (y=0)
          wallEnd: pixelHeight, // Floor
          measurementLinePos: leftX,
          baseOffset: 40, // Start 40px out from wall edge
          scale,
          measurementType: 'vertical',
          wallId: wall?.id || `wall_${index}`,
          invertOffset: true, // Go outward (negative x direction)
        });
        measurements.push(...leftResult.measurements);

        // Add overall wall height on left side (positioned outside all node-generated lines)
        const totalHeightOffset = -(40 + leftResult.lineCount * 40);
        measurements.push({
          type: 'vertical',
          subtype: 'wall-total',
          start: { x: leftX, y: 0 },
          end: { x: leftX, y: pixelHeight },
          distance: height,
          label: 'Total Height',
          offset: totalHeightOffset,
          priority: 1,
          wallId: wall?.id || `wall_${index}`,
          fromPoint: { y: 0, x: leftX, type: 'ceiling', label: 'Ceiling' },
          toPoint: { y: pixelHeight, x: leftX, type: 'floor', label: 'Floor' },
        });
      }

      // Generate RIGHT measurements (last segment or single wall)
      if (!isGroupedWalls || isLastSegment) {
        const rightX = pixelX + pixelWidth;
        const rightResult = generateMeasurementsForSide({
          nodes,
          typesWithNodes,
          side: 'right',
          labels: {
            startEdge: 'Top',
            endEdge: 'Bottom',
            wallStart: 'Ceiling',
            wallEnd: 'Floor',
          },
          wallStart: 0, // Ceiling (y=0)
          wallEnd: pixelHeight, // Floor
          measurementLinePos: rightX,
          baseOffset: 40, // Start 40px out from wall edge
          scale,
          measurementType: 'vertical',
          wallId: wall?.id || `wall_${index}`,
          invertOffset: false, // Go outward (positive x direction)
        });
        measurements.push(...rightResult.measurements);
      }
    }
  });

  return measurements;
};

// --- Auto-stagger layout for elevation labels (px screen-space) ------------
const ELEV_LAYOUT_PAD_PX = 3; // px added to each label half-width (along-edge)
const ELEV_LAYOUT_GAP_PX = 4; // px min clear space between two labels in one row
const ELEV_LAYOUT_BASE_OFFSET_PX = 40; // innermost dim line offset (px)
const ELEV_LAYOUT_ROW_PITCH_PX = 24; // per-slot pitch in px (was generator lineSpacing=40, tightened for label-sized stagger)
const ELEV_LAYOUT_TIER_TOL_PX = 2; // px — same tier within this perpendicular distance
const ELEV_LAYOUT_PERP_TOL_PX = 1; // px — same anchor line within this perp distance

/**
 * Auto-stagger elevation measurement labels by re-assigning `offset` to keep
 * overlapping labels from colliding. Mirrors floor-plan `layoutMeasurements`,
 * but operates in screen-pixel space against the elevation measurement shape
 * (`type`, `start`, `end`, `offset`, `wallId`).
 *
 * Each measurement is grouped by (type, wallId, anchor side, sign(offset)) so
 * top vs bottom (horizontal) and left vs right (vertical) stagger independently.
 * Within a group, items are clustered into tiers by |offset| then greedily packed
 * along the dim line by [proj-half, proj+half] to assign one slot. The final
 * offset is `sign * (baseOffsetPx + slot * rowPitchPx)`, in px.
 *
 * Pure: returns a new array with re-placed offsets. Items that already collide
 * at their generator-assigned tier get pushed outward by additional slots; items
 * that don't collide keep their generator-assigned tier (collapsed onto the
 * tier's slot index).
 *
 * @param {Array} measurements
 * @param {{
 *   measureWidth?: (label: string, fontPx: number) => number,
 *   fontPx?: number,
 *   baseOffsetPx?: number,
 *   rowPitchPx?: number,
 * }} opts
 * @returns {Array}
 */
export const layoutElevationMeasurements = (measurements, opts = {}) => {
  if (!Array.isArray(measurements) || measurements.length === 0) {
    return measurements ?? [];
  }
  const {
    measureWidth,
    fontPx = 12,
    baseOffsetPx = ELEV_LAYOUT_BASE_OFFSET_PX,
    rowPitchPx = ELEV_LAYOUT_ROW_PITCH_PX,
  } = opts;

  // Label width in svg-units. Positions in the elevation SVG don't scale with
  // zoom (the viewer applies CSS transform), so we pack in raw svg-unit space.
  const widthOf =
    typeof measureWidth === 'function'
      ? (label) => measureWidth(label, fontPx)
      : (label) => String(label ?? '').length * fontPx * 0.5;

  // 1. Derive per-measurement geometry + group by (type, wallId, anchor, sign).
  const groups = [];
  const entries = measurements.map((m) => {
    const type = m.type;
    const sign = (m.offset ?? 0) < 0 ? -1 : 1;
    let proj;
    let anchorPerp;
    let lineHalf;
    if (type === 'horizontal') {
      proj = ((m.start?.x ?? 0) + (m.end?.x ?? 0)) / 2;
      anchorPerp = m.start?.y ?? 0;
      lineHalf = Math.abs((m.end?.x ?? 0) - (m.start?.x ?? 0)) / 2;
    } else if (type === 'vertical') {
      proj = ((m.start?.y ?? 0) + (m.end?.y ?? 0)) / 2;
      anchorPerp = m.start?.x ?? 0;
      lineHalf = Math.abs((m.end?.y ?? 0) - (m.start?.y ?? 0)) / 2;
    } else {
      proj = 0;
      anchorPerp = 0;
      lineHalf = 0;
    }
    // Tier dim is forced to 0: input `m.offset` is the *generator-assigned*
    // distinct offset (40/80/120…), so keying tiers on |offset| would put each
    // item in its own tier with slot=0 and the row-packing collision logic
    // never runs. Collapse everything into a single tier and let the
    // screen-space row packing in `assignStaggerSlots` lift overlapping labels
    // into higher rows. The rendered offset is rewritten below from `slot`.
    const tierDist = 0;
    // The elevation generator never sets `m.label` — only `fromLabel`/`toLabel`.
    // The SVG renders `metersToFeetInches(m.distance)` (see
    // `createMeasurementSVGElements` and the inline elevation renderer in
    // `RenderWallElevation.js`). Use that exact string for collision width so
    // row packing actually sees the rendered label size.
    const renderedLabel =
      typeof m.distance === 'number' ? metersToFeetInches(m.distance) : '';
    const labelHalf = widthOf(renderedLabel) / 2;
    // Row-collision check operates on the FULL dim-line extent (start → end),
    // not just the label width. Two adjacent measurements with non-overlapping
    // labels can still have overlapping line spans (e.g. A=[0..100],
    // B=[90..180] — labels at centers 50 and 135 don't touch, but lines
    // overlap at [90..100]). Use the larger of (line half, label half) so
    // very short measurements with long-format labels still get pushed apart
    // by the label width.
    const half = Math.max(lineHalf, labelHalf) + ELEV_LAYOUT_PAD_PX;

    // Find or create a group with matching type/wallId/anchor/sign.
    let group = null;
    for (const g of groups) {
      if (
        g.type === type &&
        g.wallId === m.wallId &&
        g.sign === sign &&
        Math.abs(g.anchorPerp - anchorPerp) <= ELEV_LAYOUT_PERP_TOL_PX
      ) {
        group = g;
        break;
      }
    }
    if (!group) {
      group = {
        type,
        wallId: m.wallId,
        sign,
        anchorPerp,
        items: [],
      };
      groups.push(group);
    }
    const entry = { tierDist, proj, half, sign, slot: 0 };
    group.items.push(entry);
    return entry;
  });

  // 2. Per group: tier-cluster + greedy row packing → single slot index.
  assignStaggerSlots(groups, {
    tierTol: ELEV_LAYOUT_TIER_TOL_PX,
    gap: ELEV_LAYOUT_GAP_PX,
  });

  // 3. Re-place offset = sign * (baseOffsetPx + slot * rowPitchPx).
  return measurements.map((m, index) => {
    const { slot, sign } = entries[index];
    return {
      ...m,
      offset: sign * (baseOffsetPx + slot * rowPitchPx),
      layoutSlot: slot,
    };
  });
};

/**
 * Key for one dimension STRING — the stack of parallel dim lines sharing a
 * type, wall, anchor edge and side. Matches the grouping
 * `layoutElevationMeasurements` does internally, close enough for slot
 * bookkeeping (its anchor tolerance is 1px, so rounding agrees).
 */
const dimensionStringKey = (m) => {
  const sign = (m.offset ?? 0) < 0 ? -1 : 1;
  const anchor =
    m.type === 'horizontal'
      ? Math.round(m.start?.y ?? 0)
      : Math.round(m.start?.x ?? 0);
  return `${m.type}|${m.wallId}|${anchor}|${sign}`;
};

/**
 * Lay out the HIDDEN measurements outboard of an already-laid-out visible set.
 *
 * Revealing the hidden ones must not move a line that is actually on the
 * drawing: `layoutElevationMeasurements` packs each dimension string by
 * along-edge position, so folding the hidden items into the same call would
 * re-shuffle the visible slots and the drawing would jump every time the
 * Hidden Measurements panel opened. Instead the ghosts are staggered among
 * themselves and then pushed past the outermost visible slot on their own
 * dimension string.
 *
 * Pure. `visible` must already carry `layoutSlot` (i.e. be the OUTPUT of
 * `layoutElevationMeasurements`); pass `[]` when the wall has no visible
 * measurements and the ghosts take the innermost slots themselves.
 *
 * @param {Array} visible  laid-out visible measurements (carry `layoutSlot`)
 * @param {Array} hidden   raw hidden measurements, generator offsets intact
 * @param {object} opts    same options as `layoutElevationMeasurements`
 * @returns {Array} hidden measurements with ghost `offset`s
 */
export const layoutHiddenElevationMeasurements = (
  visible,
  hidden,
  opts = {}
) => {
  if (!Array.isArray(hidden) || hidden.length === 0) return [];
  const rowPitchPx = opts.rowPitchPx ?? ELEV_LAYOUT_ROW_PITCH_PX;

  const outermostVisibleSlot = new Map();
  (visible || []).forEach((m) => {
    const key = dimensionStringKey(m);
    const slot = m.layoutSlot ?? 0;
    if (
      !outermostVisibleSlot.has(key) ||
      outermostVisibleSlot.get(key) < slot
    ) {
      outermostVisibleSlot.set(key, slot);
    }
  });

  return layoutElevationMeasurements(hidden, opts).map((m) => {
    const key = dimensionStringKey(m);
    // One clear row past the last visible line, or the ghost's own slot when
    // this dimension string has nothing visible left on it.
    const push = outermostVisibleSlot.has(key)
      ? outermostVisibleSlot.get(key) + 1
      : 0;
    const sign = (m.offset ?? 0) < 0 ? -1 : 1;
    return { ...m, offset: m.offset + sign * push * rowPitchPx };
  });
};

/**
 * Draw elevation measurements on a canvas or SVG
 * @param {CanvasRenderingContext2D|SVGElement} ctx - Drawing context
 * @param {Array} measurements - Array of measurement objects
 * @param {number} scale - Current zoom scale
 * @param {Object} options - Drawing options
 */
export const drawElevationMeasurements = (
  ctx,
  measurements,
  scale,
  options = {}
) => {
  const {
    color = '#FF6B35',
    fontSize = 12,
    lineWidth = 1,
    showLabels = true,
  } = options;

  // If using canvas
  if (ctx.stroke) {
    ctx.save();
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = lineWidth / scale;
    ctx.font = `${fontSize}px Arial`;

    measurements.forEach((measurement) => {
      const { type, start, end, distance, label, offset } = measurement;

      // Draw measurement line
      ctx.beginPath();
      if (type === 'horizontal') {
        const y = start.y + (offset || 0);
        ctx.moveTo(start.x, y);
        ctx.lineTo(end.x, y);

        // Draw end caps
        ctx.moveTo(start.x, y - 5);
        ctx.lineTo(start.x, y + 5);
        ctx.moveTo(end.x, y - 5);
        ctx.lineTo(end.x, y + 5);
      } else if (type === 'vertical') {
        const x = start.x + (offset || 0);
        ctx.moveTo(x, start.y);
        ctx.lineTo(x, end.y);

        // Draw end caps
        ctx.moveTo(x - 5, start.y);
        ctx.lineTo(x + 5, start.y);
        ctx.moveTo(x - 5, end.y);
        ctx.lineTo(x + 5, end.y);
      }
      ctx.stroke();

      // Draw text
      if (showLabels) {
        const text = metersToFeetInches(distance);
        const textX =
          type === 'horizontal'
            ? (start.x + end.x) / 2
            : start.x + (offset || 0) + 10;
        const textY =
          type === 'horizontal'
            ? start.y + (offset || 0) - 5
            : (start.y + end.y) / 2;

        // Draw text background
        const metrics = ctx.measureText(text);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.fillRect(
          textX - metrics.width / 2 - 2,
          textY - fontSize + 2,
          metrics.width + 4,
          fontSize + 4
        );

        // Draw text
        ctx.fillStyle = color;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(text, textX, textY);
      }
    });

    ctx.restore();
  }
};

// Helper functions
export const isOpening = (obj) => {
  const type = getObjectTypeLabel(obj).toLowerCase();
  return type === 'door' || type === 'window';
};

export const getObjectWidth = (obj, scale = 100) => {
  // Use pre-calculated renderedWidth if available (already in pixels, accounts for view angle)
  if (obj.renderedWidth) return obj.renderedWidth;

  // Fall back to dimensions if renderedWidth not available
  if (obj.dimensions) {
    if (obj.dimensions.x) return obj.dimensions.x * scale;
    if (obj.dimensions.width) return obj.dimensions.width * scale;
    if (Array.isArray(obj.dimensions)) return obj.dimensions[0] * scale;
  }
  const fixtureType = getNormalizedFixtureType(obj) || 'unknown';
  console.error(
    `[elevationMeasurementUtils] No width found for fixture type "${fixtureType}", id: ${obj.id || 'unknown'}. Using fallback of 50px.`
  );
  return 50; // Default width in pixels
};

export const getObjectHeight = (obj, scale = 100) => {
  // Use pre-calculated renderedHeight if available (already in pixels)
  if (obj.renderedHeight) return obj.renderedHeight;

  // Fall back to dimensions if renderedHeight not available
  if (obj.dimensions) {
    if (obj.dimensions.y) return obj.dimensions.y * scale;
    if (obj.dimensions.height) return obj.dimensions.height * scale;
    if (Array.isArray(obj.dimensions)) return obj.dimensions[1] * scale;
  }
  const fixtureType = getNormalizedFixtureType(obj) || 'unknown';
  console.error(
    `[elevationMeasurementUtils] No height found for fixture type "${fixtureType}", id: ${obj.id || 'unknown'}. Using fallback of 50px.`
  );
  return 50; // Default height in pixels
};

/**
 * A pointerdown that reaches the elevations viewport starts a canvas pan
 * (`useElevationsGestures.handlePointerDown`). Measurement dragging is wired on
 * MOUSE events, and `stopPropagation()` on a mousedown does NOT stop the
 * pointerdown that already fired ahead of it — so the pan runs in lockstep with
 * the drag, translating the SVG by exactly the cursor delta, which leaves the
 * cursor over the same SVG point and the measurement's offset unchanged. Every
 * drag-capable element therefore stops the pointerdown as well.
 */
export const stopCanvasPan = (e) => e.stopPropagation();

/**
 * Create SVG elements for measurements (React components)
 */
export const createMeasurementSVGElements = (
  measurements,
  scale,
  options = {}
) => {
  const {
    color = '#0066cc', // Use same blue as UV floor plan for consistency
    selectedColor = HIGHLIGHT_COLORS.PRIMARY, // Pink/purple for measurements related to selected object
    fontSize = 12,
    strokeWidth = 0.5,
    showLabels = true,
    selectedObjectId = null, // Highlight measurements connected to this object
    selectedMeasurement = null, // The currently selected/clicked measurement
    onMeasurementClick = null,
    onMeasurementMouseDown = null,
    onLabelMouseDown = null, // Handler for label dragging
    isDragging = false,
    draggedIndex = null,
    draggedLabelIndex = null, // Track which label is being dragged
    labelOffsets = {}, // Store label position offsets
  } = options;

  // Helper to check if a measurement involves the selected object
  // Checks all objects in the node, not just the primary object
  const measurementInvolvesObject = (measurement, objectId) => {
    if (!objectId) return false;

    // Check fromPoint - could be a single object or array of objects
    const fromObjects = measurement.fromPoint?.objects || [];
    const fromObj = measurement.fromPoint?.object;
    const fromMatch =
      fromObj?.id === objectId ||
      fromObjects.some((item) => item.obj?.id === objectId);

    // Check toPoint - could be a single object or array of objects
    const toObjects = measurement.toPoint?.objects || [];
    const toObj = measurement.toPoint?.object;
    const toMatch =
      toObj?.id === objectId ||
      toObjects.some((item) => item.obj?.id === objectId);

    return fromMatch || toMatch;
  };

  // Helper to check if a measurement matches the selected measurement
  const isMeasurementSelected = (measurement) => {
    if (!selectedMeasurement) return false;
    return (
      measurement.type === selectedMeasurement.type &&
      measurement.distance === selectedMeasurement.distance &&
      measurement.start?.x === selectedMeasurement.start?.x &&
      measurement.start?.y === selectedMeasurement.start?.y &&
      measurement.end?.x === selectedMeasurement.end?.x &&
      measurement.end?.y === selectedMeasurement.end?.y
    );
  };

  // Inverse scale to counteract CSS transform scaling
  // For font size: at 3x zoom and above, text stays constant screen size
  // Below 3x zoom, text scales with the drawing (gets smaller on screen)
  const referenceScale = 2; // The zoom level where text size is "locked"
  const scaledStrokeWidth = strokeWidth / scale;
  const scaledFontSize =
    scale >= referenceScale ? fontSize / scale : fontSize / referenceScale;
  const scaledCapLength = 5 / scale;

  return measurements
    .map((measurement, index) => {
      const { type, start, end, distance, label, offset, subtype } =
        measurement;
      const elements = [];

      // Check if this measurement is related to the selected object
      const isRelatedToSelected = measurementInvolvesObject(
        measurement,
        selectedObjectId
      );

      // Check if this is the directly selected/clicked measurement
      const isThisMeasurementSelected = isMeasurementSelected(measurement);

      const isBeingDragged = draggedIndex === index;
      const canDrag = onMeasurementMouseDown && !isDragging;

      // Color: highlight for selected measurement, orange for dragging, blue otherwise
      let measurementColor = isThisMeasurementSelected
        ? HIGHLIGHT_COLORS.PRIMARY
        : isBeingDragged
          ? '#ff6b35'
          : color;

      // Stroke width multiplier: 5x for selected measurement or related to selected object, 1.5x for dragging, 1x otherwise
      const strokeMultiplier = isThisMeasurementSelected
        ? 5
        : isRelatedToSelected
          ? 5
          : isBeingDragged
            ? 1.5
            : 1;

      if (type === 'horizontal') {
        const y = start.y + (offset || 0);

        // Add invisible interaction area for dragging (thicker line)
        if (onMeasurementMouseDown) {
          elements.push({
            type: 'line',
            key: `${index}-drag-area`,
            props: {
              x1: start.x,
              y1: y,
              x2: end.x,
              y2: y,
              stroke: 'transparent',
              strokeWidth: Math.max(20 / scale, scaledStrokeWidth * 10),
              style: {
                cursor: canDrag
                  ? 'ns-resize'
                  : isBeingDragged
                    ? 'grabbing'
                    : 'pointer',
                pointerEvents: 'stroke',
              },
              className: 'measurement-drag-clickable',
              onPointerDown: stopCanvasPan,
              onMouseDown: (e) => onMeasurementMouseDown(e, measurement, index),
              onClick: onMeasurementClick
                ? () => onMeasurementClick(measurement)
                : undefined,
            },
          });
        }

        // Main line
        elements.push({
          type: 'line',
          key: `${index}-main`,
          props: {
            x1: start.x,
            y1: y,
            x2: end.x,
            y2: y,
            stroke: measurementColor,
            strokeWidth: scaledStrokeWidth * strokeMultiplier,
            style: { pointerEvents: 'none' },
          },
        });

        // End caps
        elements.push({
          type: 'line',
          key: `${index}-cap1`,
          props: {
            x1: start.x,
            y1: y - scaledCapLength,
            x2: start.x,
            y2: y + scaledCapLength,
            stroke: measurementColor,
            strokeWidth: scaledStrokeWidth * strokeMultiplier,
            style: { pointerEvents: 'none' },
          },
        });

        elements.push({
          type: 'line',
          key: `${index}-cap2`,
          props: {
            x1: end.x,
            y1: y - scaledCapLength,
            x2: end.x,
            y2: y + scaledCapLength,
            stroke: measurementColor,
            strokeWidth: scaledStrokeWidth * strokeMultiplier,
            style: { pointerEvents: 'none' },
          },
        });

        // Extension lines (dotted) from measurement line UP into the drawing
        // These show what points the measurement is referring to
        // Use the actual y position of the object being measured (from fromPoint/toPoint)
        const { fromPoint, toPoint } = measurement;
        const leftTargetY = fromPoint?.y ?? start.y; // Object's y position, fallback to wall bottom
        const rightTargetY = toPoint?.y ?? start.y;

        // Left extension line - goes from top of end cap UP to the measured point
        elements.push({
          type: 'line',
          key: `${index}-ext1`,
          props: {
            x1: start.x,
            y1: y - scaledCapLength, // Start from top of end cap
            x2: start.x,
            y2: leftTargetY, // Go up to the object's y position
            stroke: measurementColor,
            strokeWidth: scaledStrokeWidth * 0.75 * strokeMultiplier,
            strokeDasharray: `${3 / scale} ${3 / scale}`,
            opacity: 0.6,
            style: { pointerEvents: 'none' },
          },
        });

        // Right extension line - goes from top of end cap UP to the measured point
        elements.push({
          type: 'line',
          key: `${index}-ext2`,
          props: {
            x1: end.x,
            y1: y - scaledCapLength, // Start from top of end cap
            x2: end.x,
            y2: rightTargetY, // Go up to the object's y position
            stroke: measurementColor,
            strokeWidth: scaledStrokeWidth * 0.75 * strokeMultiplier,
            strokeDasharray: `${3 / scale} ${3 / scale}`,
            opacity: 0.6,
            style: { pointerEvents: 'none' },
          },
        });

        // Text label with background
        if (showLabels) {
          const text = metersToFeetInches(distance);
          // A tiered label that will not fit between its own extension lines
          // slides ALONG the line (see elevationStringLabels) and takes a
          // leader back to what it measures. Rows are what the tiers cap, so a
          // label never earns one.
          const labelShift = measurement.labelAlongPx || 0;
          const lineMidX = (start.x + end.x) / 2;
          const textX = lineMidX + labelShift;
          // Tight labels alternate to the far side of the line rather than
          // travelling along it — two label rows, ONE dimension line.
          // A flipped label tucks close under its own line: any further and it
          // lands in the lane where the NEXT line draws its own labels.
          const textY = measurement.labelFlipSide
            ? y + 2 / scale + scaledFontSize
            : y - 8 / scale;

          if (labelShift) {
            elements.push({
              type: 'line',
              key: `${index}-label-leader`,
              props: {
                x1: lineMidX,
                y1: y,
                x2: textX,
                y2: textY + scaledFontSize / 4,
                stroke: measurementColor,
                strokeWidth: scaledStrokeWidth * 0.75 * strokeMultiplier,
                opacity: 0.6,
                style: { pointerEvents: 'none' },
              },
            });
          }

          // Estimate text width (rough approximation) - more accurate estimation
          const textWidth = text.length * (scaledFontSize * 0.5);
          const padding = 1 / scale; // Reduced padding from 4 to 1

          const canDragLabel = onLabelMouseDown && !isDragging;

          // Add white background rectangle - tighter fit
          elements.push({
            type: 'rect',
            key: `${index}-text-bg`,
            props: {
              x: textX - textWidth / 2 - padding,
              y: textY - scaledFontSize - padding / 4,
              width: textWidth + padding * 2,
              height: scaledFontSize + padding / 2,
              fill: isBeingDragged
                ? 'rgba(255, 235, 235, 0.95)'
                : 'rgba(255, 255, 255, 0.85)',
              stroke: isBeingDragged ? measurementColor : 'none',
              strokeWidth: isBeingDragged ? scaledStrokeWidth : 0,
              className: onMeasurementClick ? 'measurement-bg-clickable' : '',
              style: {
                cursor: isBeingDragged
                  ? 'grabbing'
                  : canDragLabel
                    ? 'ns-resize'
                    : onMeasurementClick
                      ? 'pointer'
                      : 'default',
                pointerEvents: 'auto',
              },
              onPointerDown: onLabelMouseDown ? stopCanvasPan : undefined,
              onMouseDown: onLabelMouseDown
                ? (e) => onLabelMouseDown(e, measurement, index)
                : undefined,
              onClick: onMeasurementClick
                ? () => {
                    onMeasurementClick(measurement);
                  }
                : undefined,
            },
          });

          // Add text
          elements.push({
            type: 'text',
            key: `${index}-text`,
            props: {
              x: textX,
              y: textY,
              fill: measurementColor,
              fontSize: scaledFontSize,
              fontFamily: 'Arial, sans-serif',
              fontWeight:
                isThisMeasurementSelected ||
                isRelatedToSelected ||
                isBeingDragged
                  ? 'bold'
                  : '500',
              textAnchor: 'middle',
              dominantBaseline: 'bottom',
              className: onMeasurementClick
                ? 'measurement-label-clickable'
                : '',
              style: {
                cursor: isBeingDragged
                  ? 'grabbing'
                  : canDragLabel
                    ? 'ns-resize'
                    : onMeasurementClick
                      ? 'pointer'
                      : 'default',
                pointerEvents: 'auto',
                userSelect: 'none',
              },
              onPointerDown: onLabelMouseDown ? stopCanvasPan : undefined,
              onMouseDown: onLabelMouseDown
                ? (e) => onLabelMouseDown(e, measurement, index)
                : undefined,
              onClick: onMeasurementClick
                ? () => {
                    onMeasurementClick(measurement);
                  }
                : undefined,
            },
            content: text,
          });
        }
      } else if (type === 'vertical') {
        const x = start.x + (offset || 0);

        // Add invisible interaction area for dragging (thicker line)
        if (onMeasurementMouseDown) {
          elements.push({
            type: 'line',
            key: `${index}-drag-area`,
            props: {
              x1: x,
              y1: start.y,
              x2: x,
              y2: end.y,
              stroke: 'transparent',
              strokeWidth: Math.max(20 / scale, scaledStrokeWidth * 10),
              style: {
                cursor: canDrag
                  ? 'ew-resize'
                  : isBeingDragged
                    ? 'grabbing'
                    : 'pointer',
                pointerEvents: 'stroke',
              },
              className: 'measurement-drag-clickable',
              onPointerDown: stopCanvasPan,
              onMouseDown: (e) => onMeasurementMouseDown(e, measurement, index),
              onClick: onMeasurementClick
                ? () => onMeasurementClick(measurement)
                : undefined,
            },
          });
        }

        // Main line
        elements.push({
          type: 'line',
          key: `${index}-main`,
          props: {
            x1: x,
            y1: start.y,
            x2: x,
            y2: end.y,
            stroke: measurementColor,
            strokeWidth: scaledStrokeWidth * strokeMultiplier,
            style: { pointerEvents: 'none' },
          },
        });

        // End caps
        elements.push({
          type: 'line',
          key: `${index}-cap1`,
          props: {
            x1: x - scaledCapLength,
            y1: start.y,
            x2: x + scaledCapLength,
            y2: start.y,
            stroke: measurementColor,
            strokeWidth: scaledStrokeWidth * strokeMultiplier,
            style: { pointerEvents: 'none' },
          },
        });

        elements.push({
          type: 'line',
          key: `${index}-cap2`,
          props: {
            x1: x - scaledCapLength,
            y1: end.y,
            x2: x + scaledCapLength,
            y2: end.y,
            stroke: measurementColor,
            strokeWidth: scaledStrokeWidth * strokeMultiplier,
            style: { pointerEvents: 'none' },
          },
        });

        // Extension lines (dotted) from measurement line INTO the drawing
        // These show what points the measurement is referring to
        // Use the actual x position of the object being measured (from fromPoint/toPoint)
        const { fromPoint, toPoint } = measurement;
        const topTargetX = fromPoint?.x ?? start.x; // Object's x position, fallback to wall edge
        const bottomTargetX = toPoint?.x ?? start.x;

        // Top extension line - goes from end cap INTO the drawing to the object
        elements.push({
          type: 'line',
          key: `${index}-ext1`,
          props: {
            x1: x + scaledCapLength, // Start from right side of end cap
            y1: start.y,
            x2: topTargetX, // Go to the object's x position
            y2: start.y,
            stroke: measurementColor,
            strokeWidth: scaledStrokeWidth * 0.75 * strokeMultiplier,
            strokeDasharray: `${3 / scale} ${3 / scale}`,
            opacity: 0.6,
            style: { pointerEvents: 'none' },
          },
        });

        // Bottom extension line - goes from end cap INTO the drawing to the object
        elements.push({
          type: 'line',
          key: `${index}-ext2`,
          props: {
            x1: x + scaledCapLength, // Start from right side of end cap
            y1: end.y,
            x2: bottomTargetX, // Go to the object's x position
            y2: end.y,
            stroke: measurementColor,
            strokeWidth: scaledStrokeWidth * 0.75 * strokeMultiplier,
            strokeDasharray: `${3 / scale} ${3 / scale}`,
            opacity: 0.6,
            style: { pointerEvents: 'none' },
          },
        });

        // Text label with background and rotation for vertical text
        if (showLabels) {
          const text = metersToFeetInches(distance);
          const labelShift = measurement.labelAlongPx || 0;
          const lineMidY = (start.y + end.y) / 2;
          const midY = lineMidY + labelShift;
          // See the horizontal branch: the far side of the line is the second
          // label row.
          const textX = measurement.labelFlipSide
            ? x + 2 / scale + scaledFontSize
            : x - 8 / scale;

          if (labelShift) {
            elements.push({
              type: 'line',
              key: `${index}-label-leader`,
              props: {
                x1: x,
                y1: lineMidY,
                x2: textX,
                y2: midY,
                stroke: measurementColor,
                strokeWidth: scaledStrokeWidth * 0.75 * strokeMultiplier,
                opacity: 0.6,
                style: { pointerEvents: 'none' },
              },
            });
          }

          // Estimate text width (rough approximation) - more accurate estimation
          const textWidth = text.length * (scaledFontSize * 0.5);
          const padding = 1 / scale; // Reduced padding from 4 to 1

          const canDragLabel = onLabelMouseDown && !isDragging;

          // Add white background rectangle (will be rotated with text) - tighter fit
          elements.push({
            type: 'rect',
            key: `${index}-text-bg`,
            props: {
              x: textX - textWidth / 2,
              y: midY - scaledFontSize / 2 - padding / 4,
              width: textWidth + padding * 2,
              height: scaledFontSize + padding / 2,
              fill: isBeingDragged
                ? 'rgba(255, 235, 235, 0.95)'
                : 'rgba(255, 255, 255, 0.85)',
              stroke: isBeingDragged ? measurementColor : 'none',
              strokeWidth: isBeingDragged ? scaledStrokeWidth : 0,
              transform: `rotate(-90 ${textX} ${midY})`,
              className: onMeasurementClick ? 'measurement-bg-clickable' : '',
              style: {
                cursor: isBeingDragged
                  ? 'grabbing'
                  : canDragLabel
                    ? 'ew-resize'
                    : onMeasurementClick
                      ? 'pointer'
                      : 'default',
                pointerEvents: 'auto',
              },
              onPointerDown: onLabelMouseDown ? stopCanvasPan : undefined,
              onMouseDown: onLabelMouseDown
                ? (e) => onLabelMouseDown(e, measurement, index)
                : undefined,
              onClick: onMeasurementClick
                ? () => {
                    onMeasurementClick(measurement);
                  }
                : undefined,
            },
          });

          // Add text
          elements.push({
            type: 'text',
            key: `${index}-text`,
            props: {
              x: textX,
              y: midY,
              fill: measurementColor,
              fontSize: scaledFontSize,
              fontFamily: 'Arial, sans-serif',
              fontWeight:
                isThisMeasurementSelected ||
                isRelatedToSelected ||
                isBeingDragged
                  ? 'bold'
                  : '500',
              textAnchor: 'middle',
              dominantBaseline: 'middle',
              transform: `rotate(-90 ${textX} ${midY})`,
              className: onMeasurementClick
                ? 'measurement-label-clickable'
                : '',
              style: {
                cursor: isBeingDragged
                  ? 'grabbing'
                  : canDragLabel
                    ? 'ew-resize'
                    : onMeasurementClick
                      ? 'pointer'
                      : 'default',
                pointerEvents: 'auto',
                userSelect: 'none',
              },
              onPointerDown: onLabelMouseDown ? stopCanvasPan : undefined,
              onMouseDown: onLabelMouseDown
                ? (e) => onLabelMouseDown(e, measurement, index)
                : undefined,
              onClick: onMeasurementClick
                ? () => {
                    onMeasurementClick(measurement);
                  }
                : undefined,
            },
            content: text,
          });
        }
      }

      return elements;
    })
    .flat();
};

// --- Hidden ("removed") measurement ghosts ---------------------------------

// Grey, deliberately outside the blue/pink measurement palette: a ghost must
// never read as a dimension the drawing actually carries.
const HIDDEN_MEASUREMENT_COLOR = '#9a9a9a';
const HIDDEN_MEASUREMENT_OPACITY = 0.75;
const HIDDEN_EYE_WIDTH_PX = 13; // svg units at scale 1, matches the 14px label
const HIDDEN_EYE_GAP_PX = 5; // clear space between the eye and the label box

/**
 * Crossed-out eye badge — the "this one is hidden" marker, drawn to the left
 * of a ghost's label. Returns the same `{ type, key, props }` shape as the
 * rest of the builder, so the caller's element mapper places it.
 */
const createHiddenEyeElements = (
  keyBase,
  cx,
  cy,
  width,
  strokeWidth,
  transform
) => {
  const halfW = width / 2;
  const halfH = width * 0.31;
  const shared = {
    stroke: HIDDEN_MEASUREMENT_COLOR,
    strokeWidth,
    opacity: HIDDEN_MEASUREMENT_OPACITY,
    ...(transform ? { transform } : {}),
    style: { pointerEvents: 'none' },
  };
  return [
    {
      type: 'path',
      key: `${keyBase}-eye`,
      props: {
        d:
          `M ${cx - halfW} ${cy} Q ${cx} ${cy - halfH * 2} ${cx + halfW} ${cy} ` +
          `Q ${cx} ${cy + halfH * 2} ${cx - halfW} ${cy} Z`,
        fill: 'none',
        ...shared,
      },
    },
    {
      type: 'circle',
      key: `${keyBase}-eye-pupil`,
      props: {
        cx,
        cy,
        r: width * 0.16,
        fill: HIDDEN_MEASUREMENT_COLOR,
        stroke: 'none',
        opacity: HIDDEN_MEASUREMENT_OPACITY,
        ...(transform ? { transform } : {}),
        style: { pointerEvents: 'none' },
      },
    },
    {
      type: 'line',
      key: `${keyBase}-eye-slash`,
      props: {
        x1: cx - halfW,
        y1: cy + halfW * 0.8,
        x2: cx + halfW,
        y2: cy - halfW * 0.8,
        ...shared,
      },
    },
  ];
};

/**
 * SVG elements for the measurements the user has HIDDEN on this wall, drawn as
 * grey dashed ghosts with a crossed-out eye beside each label.
 *
 * Built from `createMeasurementSVGElements` rather than beside it, so a ghost
 * is by construction the same drawing as the real thing — only greyed, dashed
 * and inert. Every interaction option is forced off: a ghost is not clickable
 * and not draggable, and restoring still goes through the Hidden Measurements
 * panel. Keys are prefixed so ghosts and real measurements can be concatenated
 * into one SVG without colliding.
 *
 * @param {Array} measurements  hidden measurements, already laid out by
 *   `layoutHiddenElevationMeasurements`
 * @param {number} scale  zoom scale, as for `createMeasurementSVGElements`
 * @param {{ fontSize?: number, strokeWidth?: number }} options
 * @returns {Array<{type: string, key: string, props: object, content?: string}>}
 */
export const createHiddenMeasurementSVGElements = (
  measurements,
  scale,
  options = {}
) => {
  const base = createMeasurementSVGElements(measurements, scale, {
    ...options,
    color: HIDDEN_MEASUREMENT_COLOR,
    showLabels: true,
    // A ghost answers to nothing: no selection highlight, no drag, no click.
    selectedObjectId: null,
    selectedMeasurement: null,
    onMeasurementClick: null,
    onMeasurementMouseDown: null,
    onLabelMouseDown: null,
    isDragging: false,
    draggedIndex: null,
  });

  const scaledStroke = (options.strokeWidth ?? 0.5) / scale;
  const dash = `${5 / scale} ${4 / scale}`;

  const ghosts = base.map((el) => {
    const props = { ...el.props, opacity: HIDDEN_MEASUREMENT_OPACITY };
    // Dash the solid runs (dim line + end caps). The extension lines already
    // carry their own finer dash pattern — leave those as drawn.
    if (el.type === 'line' && props.strokeDasharray === undefined) {
      props.strokeDasharray = dash;
    }
    return { ...el, key: `hidden-${el.key}`, props };
  });

  // One eye per label, anchored to the label's own background box so it tracks
  // the text however the label was placed. A vertical label's box carries a
  // `rotate(-90 …)`; the eye inherits it verbatim, so "just left of the text"
  // stays true in the label's own reading frame instead of drifting along the
  // unrotated axis as the label gets longer.
  const eyeWidth = HIDDEN_EYE_WIDTH_PX / scale;
  const badges = base
    .filter((el) => el.type === 'rect' && el.key.endsWith('-text-bg'))
    .map((bg) =>
      createHiddenEyeElements(
        `hidden-${bg.key}`,
        bg.props.x - HIDDEN_EYE_GAP_PX / scale - eyeWidth / 2,
        bg.props.y + bg.props.height / 2,
        eyeWidth,
        scaledStroke,
        bg.props.transform
      )
    )
    .flat();

  return [...ghosts, ...badges];
};
